import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import { PASOS_CIRCULO } from './geometry-mapper';

// Los custom modes de MapboxDraw no tienen tipados públicos completos.

interface EstadoCirculo {
  polygon: any;
  center: [number, number] | null;
  completado: boolean;
}

/**
 * Modo custom de MapboxDraw para dibujar círculos (que la librería no
 * soporta nativamente) con UX two-click: primer click fija el centro, el
 * mouse previsualiza el radio y el segundo click lo confirma.
 *
 * El círculo vive en el store de Draw como polígono de 64 lados con
 * properties { isCircle, center, radiusM } (radio en metros) — así
 * comparte pipeline de estilos, borrado y eventos con el resto de las geometrías.
 */
export const DrawCircleMode: any = {
  onSetup(): EstadoCirculo {
    const polygon = this.newFeature({
      type: 'Feature',
      properties: { isCircle: true },
      geometry: { type: 'Polygon', coordinates: [[]] },
    });
    this.addFeature(polygon);
    this.clearSelectedFeatures();
    this.updateUIClasses({ mouse: 'add' });
    this.setActionableState({ trash: true });
    return { polygon, center: null, completado: false };
  },

  onClick(state: EstadoCirculo, e: any): void {
    const punto: [number, number] = [e.lngLat.lng, e.lngLat.lat];

    if (!state.center) {
      state.center = punto;
      return;
    }

    // Segundo click: fijar radio y terminar
    const radiusM = turf.distance(state.center, punto, { units: 'meters' });
    if (radiusM === 0) return;

    this.actualizarPoligono(state, radiusM);
    state.polygon.setProperty('center', state.center);
    state.polygon.setProperty('radiusM', radiusM);
    state.completado = true;
    // Sin selección: un círculo seleccionado renderiza sus 64 vértices;
    // el usuario puede clickearlo para seleccionarlo y moverlo
    this.changeMode('simple_select', { featureIds: [] });
  },

  onMouseMove(state: EstadoCirculo, e: any): void {
    if (!state.center || state.completado) return;
    const radiusM = turf.distance(state.center, [e.lngLat.lng, e.lngLat.lat], {
      units: 'meters',
    });
    if (radiusM === 0) return;
    this.actualizarPoligono(state, radiusM);
  },

  onKeyUp(_state: EstadoCirculo, e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.changeMode('simple_select');
    }
  },

  onTrash(state: EstadoCirculo): void {
    this.deleteFeature([state.polygon.id], { silent: true });
    this.changeMode('simple_select');
  },

  onStop(state: EstadoCirculo): void {
    this.updateUIClasses({ mouse: 'none' });
    this.activateUIButton();

    if (this.getFeature(state.polygon.id) === undefined) return;

    if (state.completado) {
      this.map.fire('draw.create', {
        features: [state.polygon.toGeoJSON()],
      });
    } else {
      // Trazo incompleto (sin radio confirmado): descartarlo
      this.deleteFeature([state.polygon.id], { silent: true });
    }
  },

  toDisplayFeatures(_state: EstadoCirculo, geojson: any, display: any): void {
    // No renderizar el polígono placeholder vacío (antes del preview):
    // un anillo inválido rompe el worker de mapbox-gl
    const anillo = geojson?.geometry?.coordinates?.[0];
    if (Array.isArray(anillo) && anillo.length >= 4) {
      display(geojson);
    }
  },

  actualizarPoligono(state: EstadoCirculo, radiusM: number): void {
    const circulo = turf.circle(state.center!, radiusM, {
      steps: PASOS_CIRCULO,
      units: 'meters',
    });
    state.polygon.incomingCoords(circulo.geometry.coordinates);
  },
};

/**
 * direct_select deformaría el círculo vértice a vértice; este override lo
 * redirige a simple_select (donde se puede mover entero, conservando el
 * radio) cuando la feature es un círculo.
 */
export const DirectSelectSinCirculos: any = {
  ...(MapboxDraw.modes as any)['direct_select'],
  onSetup(opts: any): any {
    // onSetup DEBE devolver un state válido (cambiar de modo acá rompe los
    // handlers de drag): armar el state del padre y redirigir en el
    // siguiente tick
    const state = (MapboxDraw.modes as any)['direct_select'].onSetup.call(
      this,
      opts,
    );
    const feature = this.getFeature(opts.featureId);
    if (feature?.properties?.['isCircle']) {
      setTimeout(() => {
        this.changeMode('simple_select', { featureIds: [opts.featureId] });
      }, 0);
    }
    return state;
  },
};
